import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { IoSearch, IoClose, IoAdd } from 'react-icons/io5';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import Spinner from '../../../components/common/Spinner';

const STATUS_MAP = {
  completed: { label: '완료', color: '#333' },
  in_progress: { label: '진행중', color: '#E85830' },
};

const INIT_FORM = {
  company: '',
  title: '',
  address: '',
  amount: '',
  startDate: '',
  endDate: '',
  status: 'in_progress',
};

const AdminConstructionPage = () => {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState(['전체']);
  const [company, setCompany] = useState('전체');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [companyModal, setCompanyModal] = useState(null); // 업체별 이력 모달
  const [companyTab, setCompanyTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(INIT_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const PAGE_SIZE = 20;

  // Firestore에서 시공 목록 + 승인된 파트너 업체 로드
  const fetchData = async () => {
    try {
      // 시공 목록
      const q = query(collection(db, 'constructions'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.startDate || '').localeCompare(b.startDate || ''));
      setWorks(list);

      // 승인된 파트너 파트너 목록
      const usersSnap = await getDocs(collection(db, 'users'));
      const companyNames = usersSnap.docs
        .map(d => d.data())
        .filter(u => u.status === 'approved' && u.companyName)
        .map(u => u.companyName);
      const unique = [...new Set(companyNames)].sort();
      setCompanies(['전체', ...unique]);
    } catch (err) {
      console.error('시공 목록 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = works.filter(w => {
    if (company !== '전체' && w.company !== company) return false;
    if (statusFilter !== 'all' && w.status !== statusFilter) return false;
    if (search && !(w.title || '').includes(search) && !(w.company || '').includes(search) && !(w.address || '').includes(search)) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalAmount = filtered.reduce((s, w) => s + (w.amount || 0), 0);
  const completedCount = filtered.filter(w => w.status === 'completed').length;
  const inProgressCount = filtered.filter(w => w.status === 'in_progress').length;

  const now = new Date();
  const thisMonth = now.getMonth();
  const thisYear = now.getFullYear();
  const thisMonthData = filtered.filter(w => {
    const d = w.startDate ? new Date(w.startDate) : w.createdAt?.toDate?.();
    return d && d.getMonth() === thisMonth && d.getFullYear() === thisYear;
  });
  const thisMonthCount = thisMonthData.length;
  const thisMonthAmount = thisMonthData.reduce((s, w) => s + (w.amount || 0), 0);

  const fmt = (n) => (n || 0).toLocaleString() + '원';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.company || !form.title || !form.amount) {
      alert('업체, 시공내역, 시공금액은 필수입니다.');
      return;
    }
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'constructions'), {
        company: form.company,
        title: form.title,
        address: form.address,
        amount: Number(form.amount.replace(/[^0-9]/g, '')) || 0,
        startDate: form.startDate,
        endDate: form.endDate,
        status: form.status,
        createdAt: serverTimestamp(),
      });
      setShowForm(false);
      setForm(INIT_FORM);
      await fetchData();
    } catch (err) {
      console.error('시공 등록 실패:', err);
      alert('등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return '-';
    if (typeof val === 'string') return val;
    if (val.toDate) return val.toDate().toLocaleDateString('ko-KR');
    return '-';
  };

  const startEdit = () => {
    setEditForm({
      title: modal.title || '',
      address: modal.address || '',
      amount: String(modal.amount || ''),
      startDate: modal.startDate || '',
      endDate: modal.endDate || '',
      status: modal.status || 'in_progress',
    });
    setEditing(true);
  };

  const handleUpdate = async () => {
    if (!editForm.title || !editForm.amount) {
      alert('시공내역, 시공금액은 필수입니다.');
      return;
    }
    setSubmitting(true);
    try {
      await updateDoc(doc(db, 'constructions', modal.id), {
        title: editForm.title,
        address: editForm.address,
        amount: Number(String(editForm.amount).replace(/[^0-9]/g, '')) || 0,
        startDate: editForm.startDate,
        endDate: editForm.endDate,
        status: editForm.status,
      });
      setModal(null);
      setEditing(false);
      await fetchData();
    } catch (err) {
      console.error('시공 수정 실패:', err);
      alert('수정에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('이 시공 내역을 삭제하시겠습니까?')) return;
    try {
      await deleteDoc(doc(db, 'constructions', modal.id));
      setModal(null);
      await fetchData();
    } catch (err) {
      console.error('시공 삭제 실패:', err);
      alert('삭제에 실패했습니다.');
    }
  };

  if (loading) return <Container><Spinner variant="spin" size="md" text="시공 목록 로딩 중..." fullPage /></Container>;

  return (
    <Container>
      <PageHeader>
        <HeaderRight>
          <SearchBox>
            <IoSearch size={18} color="#8C9AA4" />
            <SearchInput placeholder="시공명, 업체, 주소 검색" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </SearchBox>
          <AddBtn onClick={() => setShowForm(true)}><IoAdd size={18} /> 시공 등록</AddBtn>
        </HeaderRight>
      </PageHeader>

      {/* 요약 카드 */}
      <SummaryRow>
        <SummaryCard>
          <SumLabel>총 시공건수</SumLabel>
          <SumValue>{filtered.length}건</SumValue>
        </SummaryCard>
        <SummaryCard>
          <SumLabel>완료</SumLabel>
          <SumValue>{completedCount}건</SumValue>
        </SummaryCard>
        <SummaryCard>
          <SumLabel>진행중</SumLabel>
          <SumValue>{inProgressCount}건</SumValue>
        </SummaryCard>
        <SummaryCard>
          <SumLabel>총 시공금액</SumLabel>
          <SumValue>{(totalAmount / 10000).toLocaleString()}만원</SumValue>
        </SummaryCard>
        <SummaryCard>
          <SumLabel>이번달 시공건수</SumLabel>
          <SumValue>{thisMonthCount}건</SumValue>
        </SummaryCard>
        <SummaryCard>
          <SumLabel>이번달 시공액</SumLabel>
          <SumValue>{(thisMonthAmount / 10000).toLocaleString()}만원</SumValue>
        </SummaryCard>
      </SummaryRow>

      {/* 필터 */}
      <FilterRow>
        <FilterGroup>
          {['all', 'completed', 'in_progress'].map(s => (
            <FilterBtn key={s} $active={statusFilter === s} onClick={() => { setStatusFilter(s); setPage(1); }}>
              {s === 'all' ? '전체' : STATUS_MAP[s].label}
            </FilterBtn>
          ))}
        </FilterGroup>
        <CompanySelect value={company} onChange={e => { setCompany(e.target.value); setPage(1); }}>
          {companies.map(c => <option key={c} value={c}>{c}</option>)}
        </CompanySelect>
      </FilterRow>

      {/* 테이블 */}
      <TableWrap>
        <Table>
          <thead>
            <Tr>
              <Th>파트너</Th><Th>시공내역</Th><Th>현장주소</Th><Th>시공금액</Th><Th>시공기간</Th><Th>상태</Th>
            </Tr>
          </thead>
          <tbody>
            {paged.map(w => (
              <Tr key={w.id} $selected={selectedId === w.id} onClick={() => { setModal(w); setSelectedId(w.id); }} style={{ cursor: 'pointer' }}>
                <Td><CompanyLink onClick={e => { e.stopPropagation(); setCompanyModal(w.company); setCompanyTab('all'); }}>{w.company}</CompanyLink></Td>
                <Td>{w.title}</Td>
                <Td><AddrText>{w.address}</AddrText></Td>
                <Td><AmountText>{fmt(w.amount)}</AmountText></Td>
                <Td><DateText>{formatDate(w.startDate)} ~ {formatDate(w.endDate)}</DateText></Td>
                <Td><StatusBadge $color={STATUS_MAP[w.status]?.color || '#8C9AA4'}>{STATUS_MAP[w.status]?.label || w.status}</StatusBadge></Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && <Empty>해당 시공내역이 없습니다</Empty>}
      </TableWrap>

      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page === 1} onClick={() => setPage(1)}>«</PageBtn>
          <PageBtn disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</PageBtn>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === '...' ? <PageDots key={`dots-${i}`}>…</PageDots> :
              <PageNum key={p} $active={page === p} onClick={() => setPage(p)}>{p}</PageNum>
            )}
          <PageBtn disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</PageBtn>
          <PageBtn disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
          <PageInfo>{filtered.length}건 중 {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filtered.length)}</PageInfo>
        </Pagination>
      )}

      {/* 상세 모달 */}
      {modal && (
        <Overlay onClick={() => { setModal(null); setEditing(false); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editing ? '시공 수정' : '시공 상세'}</ModalTitle>
              <CloseBtn onClick={() => { setModal(null); setEditing(false); }}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              {editing ? (
                <>
                  <FormGroup>
                    <FormLabel>시공내역 *</FormLabel>
                    <FormInput value={editForm.title} onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))} placeholder="시공 내용" />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>현장주소</FormLabel>
                    <FormInput value={editForm.address} onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))} placeholder="현장 주소" />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>시공금액 *</FormLabel>
                    <FormInput value={editForm.amount} onChange={e => setEditForm(prev => ({ ...prev, amount: e.target.value }))} placeholder="금액 (숫자만)" inputMode="numeric" />
                  </FormGroup>
                  <FormRow>
                    <FormGroup style={{ flex: 1 }}>
                      <FormLabel>시작일</FormLabel>
                      <FormInput type="date" value={editForm.startDate} onChange={e => setEditForm(prev => ({ ...prev, startDate: e.target.value }))} />
                    </FormGroup>
                    <FormGroup style={{ flex: 1 }}>
                      <FormLabel>종료일</FormLabel>
                      <FormInput type="date" value={editForm.endDate} onChange={e => setEditForm(prev => ({ ...prev, endDate: e.target.value }))} />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <FormLabel>상태</FormLabel>
                    <FormSelect value={editForm.status} onChange={e => setEditForm(prev => ({ ...prev, status: e.target.value }))}>
                      <option value="in_progress">진행중</option>
                      <option value="completed">완료</option>
                    </FormSelect>
                  </FormGroup>
                </>
              ) : (
                <DetailTable>
                  <tbody>
                    <DRow><DLabel>파트너</DLabel><DVal>{modal.company}</DVal></DRow>
                    <DRow><DLabel>시공내역</DLabel><DVal>{modal.title}</DVal></DRow>
                    <DRow><DLabel>현장주소</DLabel><DVal>{modal.address}</DVal></DRow>
                    <DRow><DLabel>시공금액</DLabel><DVal>{fmt(modal.amount)}</DVal></DRow>
                    <DRow><DLabel>시공기간</DLabel><DVal>{formatDate(modal.startDate)} ~ {formatDate(modal.endDate)}</DVal></DRow>
                    <DRow><DLabel>상태</DLabel><DVal><StatusBadge $color={STATUS_MAP[modal.status]?.color || '#8C9AA4'}>{STATUS_MAP[modal.status]?.label || modal.status}</StatusBadge></DVal></DRow>
                  </tbody>
                </DetailTable>
              )}
            </ModalBody>
            <ModalFooter>
              {editing ? (
                <>
                  <CancelBtn onClick={() => setEditing(false)}>취소</CancelBtn>
                  <SubmitBtn onClick={handleUpdate} disabled={submitting}>{submitting ? '저장 중...' : '저장'}</SubmitBtn>
                </>
              ) : (
                <>
                  <DeleteBtn onClick={handleDelete}>삭제</DeleteBtn>
                  <SubmitBtn onClick={startEdit}>수정</SubmitBtn>
                </>
              )}
            </ModalFooter>
          </Modal>
        </Overlay>
      )}

      {/* 등록 모달 */}
      {showForm && (
        <Overlay onClick={() => setShowForm(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>시공 등록</ModalTitle>
              <CloseBtn onClick={() => setShowForm(false)}><IoClose size={22} /></CloseBtn>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>업체 *</FormLabel>
                <FormSelect name="company" value={form.company} onChange={handleChange}>
                  <option value="" disabled>업체 선택</option>
                  {companies.filter(c => c !== '전체').map(c => <option key={c} value={c}>{c}</option>)}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>시공내역 *</FormLabel>
                <FormInput name="title" value={form.title} onChange={handleChange} placeholder="시공 내용을 입력하세요" />
              </FormGroup>
              <FormGroup>
                <FormLabel>현장주소</FormLabel>
                <FormInput name="address" value={form.address} onChange={handleChange} placeholder="시공 현장 주소" />
              </FormGroup>
              <FormGroup>
                <FormLabel>시공금액 *</FormLabel>
                <FormInput name="amount" value={form.amount} onChange={handleChange} placeholder="금액 입력 (숫자만)" inputMode="numeric" />
              </FormGroup>
              <FormRow>
                <FormGroup style={{ flex: 1 }}>
                  <FormLabel>시작일</FormLabel>
                  <FormInput name="startDate" type="date" value={form.startDate} onChange={handleChange} />
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <FormLabel>종료일</FormLabel>
                  <FormInput name="endDate" type="date" value={form.endDate} onChange={handleChange} />
                </FormGroup>
              </FormRow>
              <FormGroup>
                <FormLabel>상태</FormLabel>
                <FormSelect name="status" value={form.status} onChange={handleChange}>
                  <option value="in_progress">진행중</option>
                  <option value="completed">완료</option>
                </FormSelect>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelBtn onClick={() => setShowForm(false)}>취소</CancelBtn>
              <SubmitBtn onClick={handleSubmit} disabled={submitting}>{submitting ? '등록 중...' : '등록'}</SubmitBtn>
            </ModalFooter>
          </Modal>
        </Overlay>
      )}
      {/* 업체별 이력 모달 */}
      {companyModal && (() => {
        const companyWorks = works.filter(w => w.company === companyModal);
        const tabWorks = companyTab === 'all' ? companyWorks : companyWorks.filter(w => w.status === companyTab);
        const cTotal = companyWorks.reduce((s, w) => s + (w.amount || 0), 0);
        const cCompleted = companyWorks.filter(w => w.status === 'completed').length;
        const cInProgress = companyWorks.filter(w => w.status === 'in_progress').length;
        return (
          <Overlay onClick={() => setCompanyModal(null)}>
            <CompanyModalWrap onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{companyModal} 시공이력</ModalTitle>
                <CloseBtn onClick={() => setCompanyModal(null)}><IoClose size={22} /></CloseBtn>
              </ModalHeader>

              <CompanySummary>
                <CompanySumItem>
                  <CompanySumLabel>총 시공</CompanySumLabel>
                  <CompanySumVal>{companyWorks.length}건</CompanySumVal>
                </CompanySumItem>
                <CompanySumItem>
                  <CompanySumLabel>완료</CompanySumLabel>
                  <CompanySumVal>{cCompleted}건</CompanySumVal>
                </CompanySumItem>
                <CompanySumItem>
                  <CompanySumLabel>진행중</CompanySumLabel>
                  <CompanySumVal>{cInProgress}건</CompanySumVal>
                </CompanySumItem>
                <CompanySumItem>
                  <CompanySumLabel>총 금액</CompanySumLabel>
                  <CompanySumVal>{(cTotal / 10000).toLocaleString()}만원</CompanySumVal>
                </CompanySumItem>
              </CompanySummary>

              <CompanyTabRow>
                {[
                  { key: 'all', label: '전체', count: companyWorks.length },
                  { key: 'in_progress', label: '진행중', count: cInProgress },
                  { key: 'completed', label: '완료', count: cCompleted },
                ].map(t => (
                  <CompanyTabBtn key={t.key} $active={companyTab === t.key} onClick={() => setCompanyTab(t.key)}>
                    {t.label} <span>{t.count}</span>
                  </CompanyTabBtn>
                ))}
              </CompanyTabRow>

              <CompanyList>
                {tabWorks.length === 0 ? (
                  <Empty>해당 내역이 없습니다</Empty>
                ) : (
                  tabWorks.map(w => (
                    <CompanyWorkCard key={w.id}>
                      <CWTop>
                        <CWTitle>{w.title}</CWTitle>
                        <StatusBadge $color={STATUS_MAP[w.status]?.color || '#8C9AA4'}>{STATUS_MAP[w.status]?.label || w.status}</StatusBadge>
                      </CWTop>
                      <CWMeta>
                        <span>{w.address || '-'}</span>
                        <span>{formatDate(w.startDate)} ~ {formatDate(w.endDate)}</span>
                      </CWMeta>
                      <CWAmount>{((w.amount || 0) / 10000).toLocaleString()}만원</CWAmount>
                    </CompanyWorkCard>
                  ))
                )}
              </CompanyList>
            </CompanyModalWrap>
          </Overlay>
        );
      })()}
    </Container>
  );
};

export default AdminConstructionPage;

const Container = styled.div`padding: 32px;`;
const PageHeader = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;`;
const HeaderRight = styled.div`display: flex; gap: 12px; align-items: center;`;
const SearchBox = styled.div`display: flex; align-items: center; gap: 8px; background: #fff; border: none; border-radius: 10px; padding: 8px 14px; width: 260px; box-shadow: 0 1px 3px rgba(0,0,0,0.06);`;
const SearchInput = styled.input`border: none; outline: none; font-size: 13px; flex: 1; background: transparent;`;
const AddBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 9px 18px; border-radius: 10px;
  background: #fff; color: #1E2A32; font-size: 13px; font-weight: 600;
  border: 1.5px solid #D0D0D0;
  &:hover { background: #F5F5F5; border-color: #999; }
`;

/* 요약 */
const SummaryRow = styled.div`display: grid; grid-template-columns: repeat(6, 1fr); gap: 14px; margin-bottom: 16px;`;
const SummaryCard = styled.div`
  background: #1A3A5C; border-radius: 12px; padding: 14px 16px;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.2);
  display: flex; align-items: center; justify-content: space-between;
`;
const SumLabel = styled.div`font-size: 13px; color: rgba(255,255,255,0.65);`;
const SumValue = styled.div`font-size: 22px; font-weight: 500; color: #fff;`;

/* 필터 */
const FilterRow = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`;
const FilterGroup = styled.div`display: flex; gap: 6px;`;
const FilterBtn = styled.button`
  padding: 8px 16px; border-radius: 10px; font-size: 13px; font-weight: 400;
  background: ${p => p.$active ? '#8C9AA4' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  border: none;
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
`;
const CompanySelect = styled.select`
  padding: 8px 14px; border: 1px solid #D9DDD6; border-radius: 10px;
  font-size: 13px; background: #fff; color: #1E2A32; cursor: pointer;
`;

/* 테이블 */
const TableWrap = styled.div`background: #fff; border-radius: 14px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); overflow: hidden;`;
const Table = styled.table`width: 100%; border-collapse: collapse;`;
const Tr = styled.tr`
  position: relative; transition: all 0.15s;
  &:not(:last-child) td { border-bottom: 1px solid #F0F0EC; }
  &:hover td { background: #EDF2EB; }
  &:hover td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  &:active td { background: #E3EBDF; }
  ${p => p.$selected && `
    td { background: #EDF2EB; }
    td:first-child { box-shadow: inset 3px 0 0 #4A6741; }
  `}
`;
const Th = styled.th`text-align: left; font-size: 13px; color: #8C9AA4; font-weight: 400; padding: 12px 16px; border-bottom: 1px solid #E8E8E4; background: #FAFAF7;`;
const Td = styled.td`font-size: 14px; color: #1E2A32; padding: 14px 16px; font-weight: 300;`;
const Empty = styled.div`text-align: center; padding: 48px; color: #8C9AA4; font-size: 14px;`;

const CompanyLink = styled.span`
  color: #1E2A32; text-decoration: underline; text-underline-offset: 2px;
  font-weight: 600; cursor: pointer; &:hover { color: #555; }
`;

const StatusBadge = styled.span`font-size: 15px; padding: 5px 14px; border-radius: 10px; font-weight: 400; background: ${p => p.$color}25; color: ${p => p.$color};`;
const AmountText = styled.span`font-weight: 400; color: #1E2A32;`;
const AddrText = styled.span`font-size: 12px; color: #5A6B75;`;
const DateText = styled.span`font-size: 12px; color: #8C9AA4;`;

/* 모달 */
const Overlay = styled.div`position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 200;`;
const Modal = styled.div`background: #fff; border-radius: 16px; width: 480px; max-height: 80vh; overflow-y: auto;`;
const ModalHeader = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #F0F0EC;`;
const ModalTitle = styled.h3`font-size: 18px; font-weight: 500;`;
const CloseBtn = styled.button`color: #8C9AA4; display: flex;`;
const ModalBody = styled.div`padding: 24px;`;
const ModalFooter = styled.div`display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #F0F0EC;`;
const DetailTable = styled.table`width: 100%; border-collapse: collapse; border-top: 1.5px solid #E0E0DC;`;
const DRow = styled.tr`td, th { border-bottom: 1.5px solid #E0E0DC; }`;
const DLabel = styled.th`width: 100px; font-size: 14px; color: #5A6B75; font-weight: 400; text-align: left; padding: 14px 16px; background: #F0F0EC; white-space: nowrap;`;
const DVal = styled.td`font-size: 15px; color: #1E2A32; font-weight: 400; padding: 14px 16px;`;

const FormGroup = styled.div`margin-bottom: 16px;`;
const FormRow = styled.div`display: flex; gap: 12px;`;
const FormLabel = styled.label`display: block; font-size: 13px; font-weight: 400; color: #1E2A32; margin-bottom: 6px;`;
const FormInput = styled.input`width: 100%; padding: 10px 14px; border: 1px solid #D9DDD6; border-radius: 10px; font-size: 14px;`;
const FormSelect = styled.select`width: 100%; padding: 10px 14px; border: 1px solid #D9DDD6; border-radius: 10px; font-size: 14px; background: #fff;`;
const CancelBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #F5F5F0; color: #5A6B75;`;
const SubmitBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #E8EDE6; color: #5A7A52; font-weight: 400; &:hover { background: #DDE5DA; } &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const DeleteBtn = styled.button`padding: 10px 20px; border-radius: 10px; font-size: 14px; background: #FDF2F2; color: #C0392B; &:hover { background: #FDECEA; }`;

/* 업체별 이력 모달 */
const CompanyModalWrap = styled.div`
  background: #fff; border-radius: 16px; width: 560px; max-height: 85vh;
  display: flex; flex-direction: column; overflow: hidden;
`;
const CompanySummary = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  padding: 16px 24px; background: #FAFAFA; border-bottom: 1px solid #F0F0EC;
`;
const CompanySumItem = styled.div`text-align: center;`;
const CompanySumLabel = styled.div`font-size: 11px; color: #8C9AA4; margin-bottom: 2px;`;
const CompanySumVal = styled.div`font-size: 16px; font-weight: 400; color: #1E2A32;`;
const CompanyTabRow = styled.div`
  display: flex; gap: 4px; padding: 12px 24px; border-bottom: 1px solid #F0F0EC;
`;
const CompanyTabBtn = styled.button`
  padding: 6px 14px; border-radius: 8px; font-size: 13px; font-weight: 400;
  background: ${p => p.$active ? '#8C9AA4' : '#F5F5F5'};
  color: ${p => p.$active ? '#fff' : '#888'};
  span { margin-left: 4px; font-size: 11px; opacity: 0.7; }
`;
const CompanyList = styled.div`padding: 16px 24px; overflow-y: auto; flex: 1;`;
const CompanyWorkCard = styled.div`
  padding: 14px 16px; background: #FAFAFA; border-radius: 10px;
  margin-bottom: 8px;
`;
const CWTop = styled.div`display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;`;
const CWTitle = styled.div`font-size: 14px; font-weight: 500; color: #1E2A32;`;
const CWMeta = styled.div`
  display: flex; justify-content: space-between; font-size: 12px; color: #8C9AA4; margin-bottom: 4px;
`;
const CWAmount = styled.div`font-size: 15px; font-weight: 500; color: #1E2A32; text-align: right;`;

/* 페이지네이션 */
const Pagination = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 4px;
  padding: 20px 0 8px;
`;
const PageBtn = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 14px;
  background: #fff; color: #666; border: none;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  &:disabled { opacity: 0.3; cursor: default; }
  &:hover:not(:disabled) { background: #F0F0EC; }
`;
const PageNum = styled.button`
  width: 32px; height: 32px; border-radius: 8px; font-size: 13px; font-weight: 500;
  border: none; display: flex; align-items: center; justify-content: center;
  background: ${p => p.$active ? '#4A6741' : '#fff'};
  color: ${p => p.$active ? '#fff' : '#666'};
  box-shadow: ${p => p.$active ? 'none' : '0 1px 3px rgba(0,0,0,0.06)'};
  &:hover { background: ${p => p.$active ? '#3B5534' : '#F0F0EC'}; }
`;
const PageDots = styled.span`font-size: 13px; color: #8C9AA4; padding: 0 4px;`;
const PageInfo = styled.span`font-size: 12px; color: #8C9AA4; margin-left: 12px;`;
